<!-- eslint-disable -->
<template>
  <div class="poll-widget">

    <div class="poll-badge">
      <v-icon size="13">mdi-poll</v-icon>
      {{ hasVoted ? $t('poll.pollVoted') : $t('poll.poll') }}
    </div>

    <div class="poll-question">{{ poll.question }}</div>

    <div class="poll-meta">
      {{ typeLabel }}
      <span v-if="expiryLabel"> · {{ expiryLabel }}</span>
      <span> · {{ $t('poll.votes', { count: poll.totalVotes }) }}</span>
    </div>

    <!-- Results always visible -->
    <div class="poll-results mt-3">
      <div
        v-for="opt in poll.options"
        :key="opt.optionId"
        class="poll-result-item mb-2"
      >
        <div class="result-row">
          <span class="result-label" :class="{ 'voted-label': opt.votedByUser }">
            <v-icon v-if="opt.votedByUser" size="13" class="voted-check">mdi-check-circle</v-icon>
            {{ opt.optionText }}
          </span>
          <span class="result-pct" :class="{ 'voted-pct': opt.votedByUser }">
            {{ pct(opt) }}%
          </span>
        </div>
        <div class="poll-bar-track">
          <div
            class="poll-bar-fill"
            :class="{ winner: isWinner(opt), 'user-bar': opt.votedByUser }"
            :style="{ width: pct(opt) + '%' }"
          ></div>
        </div>
        <span class="result-count">{{ opt.voteCount }} {{ $t('poll.voteNoun') }}</span>
      </div>
    </div>

    <!-- Voting controls — hidden once voted or expired -->
    <div v-if="!hasVoted && !expired" class="poll-vote-area mt-3">
      <v-divider class="mb-3" />
      <div class="text-caption text-grey mb-2">{{ $t('poll.castYourVote') }}</div>

      <div class="poll-options">
        <button
          v-for="opt in poll.options"
          :key="opt.optionId"
          class="poll-vote-btn"
          :class="{ selected: selectedIds.has(opt.optionId) }"
          @click="toggleOption(opt.optionId)"
        >
          <v-icon size="16" class="mr-2">
            {{ isMultiple
              ? (selectedIds.has(opt.optionId) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline')
              : (selectedIds.has(opt.optionId) ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank')
            }}
          </v-icon>
          {{ opt.optionText }}
        </button>
      </div>

      <v-btn
        color="primary"
        size="small"
        class="mt-2"
        :disabled="selectedIds.size === 0"
        :loading="voting"
        @click="submitVote"
      >
        {{ $t('poll.submitVote') }}
      </v-btn>
    </div>

    <!-- Expired notice -->
    <div v-else-if="expired" class="mt-2">
      <v-chip size="small" color="grey" variant="tonal">
        <v-icon start size="13">mdi-lock-outline</v-icon>
        {{ $t('poll.closed') }}
      </v-chip>
    </div>

    <!-- Voted — allow change -->
    <div v-else class="mt-2">
      <v-btn
        variant="text"
        size="x-small"
        color="grey"
        :loading="voting"
        @click="retractVote"
      >
        <v-icon start size="13">mdi-pencil-outline</v-icon>
        {{ $t('poll.changeVote') }}
      </v-btn>
    </div>

  </div>
</template>

<script setup>
/* eslint-disable */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '../services/api'

const { t } = useI18n()

const props = defineProps({
  poll: {
    type: Object,
    required: true
  },
  postId: {
    type: [Number, String],
    required: true
  }
})

const emit = defineEmits(['voted'])

const selectedIds = ref(new Set())
const voting = ref(false)

const hasVoted = computed(() => props.poll.userHasVoted)
const isMultiple = computed(() => props.poll.pollType === 'multiple')

const expired = computed(() => {
  if (!props.poll.expiresAt) return false
  return new Date(props.poll.expiresAt) < new Date()
})

const typeLabel = computed(() => {
  const map = {
    single: t('poll.singleChoice'),
    multiple: t('poll.multipleChoice'),
    yes_no: t('poll.yesNo')
  }
  return map[props.poll.pollType] ?? t('poll.singleChoice')
})

const expiryLabel = computed(() => {
  if (!props.poll.expiresAt) return null
  const diff = new Date(props.poll.expiresAt).getTime() - Date.now()
  if (diff < 0) return null
  const days = Math.floor(diff / 86400000)
  const hours = Math.floor((diff % 86400000) / 3600000)
  const mins = Math.floor((diff % 3600000) / 60000)
  if (days > 0) return t('poll.daysLeft', { count: days })
  if (hours > 0) return t('poll.hoursLeft', { count: hours })
  if (mins > 0) return t('poll.minsLeft', { count: mins })
  return t('poll.closingSoon')
})

const pct = (opt) => {
  if (!props.poll.totalVotes) return 0
  return Math.round((opt.voteCount / props.poll.totalVotes) * 100)
}

const isWinner = (opt) => {
  if (!props.poll.totalVotes) return false
  const max = Math.max(...props.poll.options.map(o => o.voteCount))
  return opt.voteCount === max && max > 0
}

const toggleOption = (id) => {
  if (!isMultiple.value) {
    selectedIds.value = new Set([id])
  } else {
    const next = new Set(selectedIds.value)
    next.has(id) ? next.delete(id) : next.add(id)
    selectedIds.value = next
  }
}

const submitVote = async () => {
  voting.value = true
  try {
    await api.post(`/polls/${props.poll.pollId}/vote`, {
      optionIds: Array.from(selectedIds.value)
    })
    selectedIds.value = new Set()
    emit('voted')
  } catch (e) {
    console.error('Vote failed', e)
  } finally {
    voting.value = false
  }
}

const retractVote = async () => {
  voting.value = true
  try {
    await api.delete(`/polls/${props.poll.pollId}/vote`)
    emit('voted')
  } catch (e) {
    console.error('Retract failed', e)
  } finally {
    voting.value = false
  }
}
</script>

<style scoped>
.poll-widget {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 14px 16px;
  background: rgba(0, 0, 0, 0.02);
}

.poll-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #e8f5e9;
  color: #2e7d32;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.poll-question {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  margin-bottom: 2px;
}

.poll-meta {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
}

.result-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.result-label {
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.voted-label {
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
}

.voted-check {
  color: rgb(var(--v-theme-primary));
}

.result-pct {
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
  min-width: 36px;
  text-align: right;
}

.voted-pct {
  color: rgb(var(--v-theme-primary));
}

.result-count {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.35);
}

.poll-bar-track {
  height: 6px;
  background: rgba(0, 0, 0, 0.07);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 2px;
}

.poll-bar-fill {
  height: 100%;
  border-radius: 3px;
  background: rgba(var(--v-theme-primary), 0.35);
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.poll-bar-fill.winner {
  background: rgba(var(--v-theme-primary), 0.7);
}

.poll-bar-fill.user-bar {
  background: rgb(var(--v-theme-primary));
}

.poll-vote-btn {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: transparent;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  margin-bottom: 6px;
  transition: background 0.15s, border-color 0.15s;
  color: inherit;
}

.poll-vote-btn:hover {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.2);
}

.poll-vote-btn.selected {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.06);
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
}
</style>